import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import cloneDeep from 'lodash/cloneDeep'
import has from 'lodash/has'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import set from 'lodash/set'
import PropTypes from 'prop-types'
import { Img as Image } from 'react-image'
import { connect } from 'react-redux'

import * as PoziUtils from 'api/utils'
import LegendGeoStyler from 'drawer/tabs/info/panels/LegendGeoStyler'
import { loadImage } from 'utils/Image'

import { SET_LAYER_LOAD_DONE, SET_LAYER_LOAD_IN_PROGRESS } from '../actions'

const styles = theme => ({
  root: {},
  table: {
    paddingBottom: 10,
    'border-collapse': 'collapse',
    'border-color': 'transparent',
    border: 'none'
  },
  bubblebackgroundtd1: {
    border: 'none',
    width: '32px',
    padding: '0px',
    'padding-top': '2px',
    'padding-bottom': '2px',
    'padding-left': '4px',
    'padding-right': '4px'
  },
  bubblebackgroundtd2: {
    border: 'none',
    padding: '0px',
    'padding-top': '2px',
    'padding-bottom': '2px',
    'padding-left': '4px',
    'padding-right': '4px'
  },
  bubblebackgroundtr: {
    border: 'none'
  },
  bubbleBackgroundImgWrapper: {
    paddingBottom: 1,
    paddingTop: 1,
    textAlign: 'center'
  },
  about: {
    padding: '6px'
  },
  layerType: {
    padding: '6px'
  },
  card: {
    minWidth: 275,
    margin: '4px',
    marginBottom: '8px'
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)'
  },
  cardTitle: {
    fontSize: 14,
    paddingBottom: 10
  },
  cardContentHeader: {
    fontWeight: 'bold'
  },
  cardContent: {
    paddingBottom: 8
  },
  cardAction: {
    padding: 0,
    paddingTop: 10,
    paddingBottom: 10
  },
  pos: {
    marginBottom: 12
  },
  expansionPanelDetails: {
    display: 'block',
    paddingLeft: 25,
    paddingRight: 25
  },
  heading: {
    paddingBottom: 10,
    paddingLeft: 10
  },
  headingIcon: {},
  rawImage: {
    maxWidth: '100%'
  },
  brokenImageIcon: {
    filter: 'invert(51%) sepia(0%) saturate(1849%) hue-rotate(208deg) brightness(93%) contrast(85%)' // https://codepen.io/sosuke/pen/Pjoqqp
  },
  geostyler: {
    // Remove border-bottom for <legend> elements, coming from forms.less
    '& legend': {
      borderBottom: 'unset',
      margin: 0
    },
    // In Firefox the SVG element needs to be constrained
    '& legend svg': {
      maxWidth: '100%'
    },
    // Hide labels that say 'Single symbol'
    '& .legend-item[title="Single symbol"] > text': {
      display: 'none'
    }
  }
})

class LayerLegend_ extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      imageData: null
    }
  }

  componentDidMount () {
    const { layerLegendUrl } = this.props
    this.checkImage(layerLegendUrl)
  }

  componentDidUpdate (prevProps) {
    const { layerLegendUrl, titleid } = this.props
    const prevLayerLegendUrl = prevProps.layerLegendUrl

    // console.debug('LayerLegend - componentDidUpdate - legend changed?', titleid, prevLayerLegendUrl !== layerLegendUrl)

    // Update legend
    if (prevLayerLegendUrl !== layerLegendUrl) {
      this.checkImage(layerLegendUrl)
    }
  }

  setLegendData (legendData = null) {
    this.setState({
      legendData
    })
  }

  checkImage = async (imageUrl) => {
    if (!imageUrl || imageUrl === '') {
      return
    }

    // console.debug('LayerLegend - checkImage', imageUrl)

    try {
      const imageDataResponse = await loadImage(imageUrl)
      const imageData = URL.createObjectURL(imageDataResponse)

      // console.debug('LayerLegend - checkImage - valid!', imageUrl)

      this.setLegendData(imageData)
    } catch (e) {
      console.log('LayerLegend - checkImage - invalid!', e, imageUrl)

      this.setLegendData(null)
    }
  }

  render () {
    const {
      classes,
      titleid,
      layerLegendUrl,
      layerLoadStatus,
      layerShowLegend,
      layerStyle,
      layerStyleGeoStyler,
      layerStyleClass,
      layerTitle,
      mapXYZ
    } = this.props

    const { legendData } = this.state

    // console.debug('Render layer legend', titleid, layerLegendUrl, layerShowLegend, layerStyle, layerStyleGeoStyler, layerTitle)

    if (!layerShowLegend) {
      // console.debug('LayerLegend - render - not rendering since showLegend set to false: ', titleid)
      return null
    }

    // Only show legend when layer is either loading (needed for WMS) or has finished loading
    if (layerLoadStatus !== SET_LAYER_LOAD_DONE && layerLoadStatus !== SET_LAYER_LOAD_IN_PROGRESS) {
      // console.debug('LayerLegend - render - not rendering since layer has not loaded: ', titleid)

      return null
    }

    // Look for GeoStyler style and use if no image legend has been provided
    const app = PoziUtils.getApp()

    let legendMarkup = null

    const defaultSize = [16, 16]
    const defaultScale = 1

    const bLegendImageDefined = !isNil(layerLegendUrl) && layerLegendUrl !== ''
    const bLegendGeoStylerDefined = !isNil(layerStyleGeoStyler) && layerStyleGeoStyler instanceof Array

    // Image Legend
    if (bLegendImageDefined) {
      if (legendData) {
        const legendAlt = `Legend image for layer '${layerTitle}'`

        legendMarkup = (
          <div className={classes.root}>
            <Image className={classes.rawImage} alt={legendAlt} src={legendData} crossOrigin="Anonymous" />
          </div>
        )
      } else {
        console.debug('Legend not valid', titleid, layerLegendUrl)
      }
    // GeoStyler Legend
    } else if (bLegendGeoStylerDefined) {
      // Workaround for an issue with the GeoStyler legend renderer
      // When the style included Text symbolizers this resulted in blank space in the legend, this code removes Text symbolizers
      const clonedLayerStyleGeoStyler = cloneDeep(layerStyleGeoStyler)
      clonedLayerStyleGeoStyler.forEach(layerStyle => {
        layerStyle.rules = layerStyle.rules.filter(rule => {
          return rule.symbolizers.some(symbolizer => symbolizer.kind !== 'Text')
        })
      })

      legendMarkup = (
        <div className={classes.root}>
          <div className={classes.geostyler}>
            <LegendGeoStyler hideTitle={true} hideRect={true} styles={clonedLayerStyleGeoStyler} layerLoadStatus={layerLoadStatus} mapXYZ={mapXYZ} titleid={titleid}/>
          </div>
        </div>
      )
    } else if (layerStyleClass) {
      // Build a HTML table with the different values
      const values = layerStyleClass.values || []

      legendMarkup = (
        <div className={classes.root}>
          <Table className={classes.table} border="0" cellSpacing="0" cellPadding="0">
            <TableBody>
              {map(values, function (style, key) {
                const styleCopy = { ...style }
                if (!has(styleCopy, 'size')) {
                  styleCopy.size = defaultSize
                }
                if (!has(styleCopy, 'scale')) {
                  styleCopy.scale = defaultScale
                }

                if (!has(styleCopy, 'bubbleBackground')) {
                  set(styleCopy, 'bubbleBackground', 'transparent')
                }
                if (styleCopy.bubbleBackground === '#blank') {
                  set(styleCopy, 'bubbleBackground', 'transparent')
                }

                const td1Style = { backgroundColor: style.bubbleBackground }

                // Resize icons to default size when they exceed that default size
                const defaultWidth = defaultSize[0] * defaultScale
                const defaultHeight = defaultSize[1] * defaultScale

                let width = styleCopy.size[0] * styleCopy.scale
                let height = styleCopy.size[1] * styleCopy.scale
                if (height > defaultHeight) {
                  const resizeScale = defaultHeight / height
                  width *= resizeScale
                  height *= resizeScale
                }

                const td1ImgStyle = { width, height }

                return (
                  <TableRow key={key} className={classes.bubblebackgroundtr}>
                    <TableCell className={classes.bubblebackgroundtd1}>
                      <div className={classes.bubbleBackgroundImgWrapper} style={td1Style}>
                        <img className={classes.rawImage} src={styleCopy.src} alt={key} style={td1ImgStyle} crossOrigin="Anonymous" />
                      </div>
                    </TableCell>
                    <TableCell className={classes.bubblebackgroundtd2}>{key}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )
    } else if (has(layerStyle, 'src')) {
      const layerStyleCopy = { ...layerStyle }
      if (!has(layerStyleCopy, 'size')) {
        set(layerStyleCopy, 'size', defaultSize)
      }

      if (!has(layerStyleCopy, 'scale')) {
        set(layerStyleCopy, 'scale', defaultScale)
      }

      if (!has(layerStyleCopy, 'bubbleBackground')) {
        set(layerStyleCopy, 'bubbleBackground', 'transparent')
      }
      if (layerStyleCopy.bubbleBackground === '#blank') {
        set(layerStyleCopy, 'bubbleBackground', 'transparent')
      }

      const td1Style = { backgroundColor: layerStyleCopy.bubbleBackground }
      const td1ImgStyle = { width: layerStyleCopy.size[0] * layerStyleCopy.scale, height: layerStyleCopy.size[1] * layerStyleCopy.scale }

      legendMarkup = (
        <div className={classes.root}>
          <Table className={classes.table} border="0" cellSpacing="0" cellPadding="0">
            <TableBody>
              <TableRow key="legend" className={classes.bubblebackgroundtr}>
                <TableCell className={classes.bubblebackgroundtd1}>
                  <div className={classes.bubbleBackgroundImgWrapper} style={td1Style}>
                    <img className={classes.rawImage} src={layerStyleCopy.src} alt="legend" style={td1ImgStyle} crossOrigin="Anonymous"/>
                  </div>
                </TableCell>
                <TableCell className={classes.bubblebackgroundtd2}>{/* layerTitle */}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )
    } else {
      // console.log('No legend found for layer', this.props.titleid)
      return null
    }

    return legendMarkup
  }
}

LayerLegend_.propTypes = {
  classes: PropTypes.object.isRequired,
  layerLegendUrl: PropTypes.string,
  layerLoadStatus: PropTypes.string,
  layerShowLegend: PropTypes.bool.isRequired,
  layerStyle: PropTypes.object,
  layerStyleGeoStyler: PropTypes.array,
  layerStyleClass: PropTypes.object,
  layerTitle: PropTypes.string.isRequired,
  mapXYZ: PropTypes.array,
  titleid: PropTypes.string.isRequired
}

function mapStateToProps (state, ownProps) {
  const { layers, mapX, mapY, mapZ } = state
  const { titleid } = ownProps

  const layerConf = layers[titleid]

  const {
    legend,
    loadstatus,
    style,
    styleClass,
    styleGeoStyler,
    showLegend,
    title
  } = layerConf

  return {
    layerLegendUrl: legend,
    layerLoadStatus: loadstatus,
    layerShowLegend: showLegend ?? true,
    layerStyle: style,
    layerStyleGeoStyler: styleGeoStyler?.style,
    layerTitle: title,
    layerStyleClass: styleClass,
    mapXYZ: [mapX, mapY, mapZ]
  }
}

const mapDispatchToProps = dispatch => {
  return {}
}

const LayerLegend = (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerLegend_)
  )
)

export default LayerLegend
